import * as THREE from 'three';
/*import { setupSceneWall } from '../setupScene.ts';

const { wall } = setupSceneWall();*/
/* ───────────────── helpers ───────────────── */
const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

type EnergyHistoryEntry = {
  time: string;
  minutesOfDay: number;   // 0..1440
  energy: number;         // kWh im Tick
  deltaHours?: number;    // Simulationsdauer dieses Ticks
  powerKW?: number;       // normalisierte Leistung für Charts: energy / deltaHours
  day: number;            // Sim-Tag
};

type PanelEntry = { type: string; energyHistory: EnergyHistoryEntry[]; totalEnergy: number; days: Set<number> };

export type EnergyTrackerFlags = {
  geometry: boolean;
  clearSky: boolean;
  clouds: boolean;
  iam: boolean;
  temperature: boolean;
  shading: boolean;
  soiling: boolean;
  pr: boolean;
  clipping: boolean;
  pushZeroAtNight: boolean;
};

export type EnergyTrackerParams = {
  keepDays: number;
  debug: boolean;
  // Atmos/Wetter
  ambientTempC: number;
  cloudiness01: number;
  // Modul/Mechanik
  iam_b0: number;
  gamma_Pmp_perK: number;
  NOCT: number;
  soilingLoss01: number;
  PR_misc: number;
  inverterLimitKW: number | null;
};

export type ShadingCallback = (name: string, panel: any, sunDir: THREE.Vector3) => number;

type ShadowOccluder = {
  mesh: THREE.Mesh;
  box: THREE.Box3;
  builderObjectId: string | null;
  dynamic: boolean;
};

/* ───────────────── EnergyTracker ───────────────── */
type NESW = 'N'|'E'|'S'|'W';
type DirKey = 'nord'|'ost'|'süd'|'west';
type TypeKey = 'fixed'|'single'|'limited'|'unlimited';

export class EnergyTracker {



  /** intern in kW */
  powerKW: number;

  trackerMap: Map<string, any>;
  panelData: Map<string, PanelEntry>;
  panelInfoMap: Map<string, any>;

  lastUpdatedDay = -1;
  simulationDay = 0;

  flags: EnergyTrackerFlags;
  params: EnergyTrackerParams;
  shadingCallback: ShadingCallback;

  /** Aggregat-Matrix: key = `${dir}-${type}` (z.B. "ost-unlimited"), Wert in kWh (seit Sim-Start) */
  private dirTypeTotals: Map<string, number>;

  /** optionaler Listener, wird am Ende von updateAll() aufgerufen */
  onMatrixUpdate?: (matrix: Map<string, number>) => void;

  /** Occluder-Meshes für Schatten-Raycasting (von außen gesetzt via setOccluders) */
  private shadowOccluders: ShadowOccluder[] = [];
  private _raycaster = new THREE.Raycaster();
  private _shadowRay = new THREE.Ray();
  private _shadowOrigin = new THREE.Vector3();
  private _shadowHits: THREE.Intersection[] = [];

  constructor(
    trackerMap: Map<string, any>,
    powerInput: number,                  // kW oder W; >50 ⇒ W → /1000
    panelInfoMap: Map<string, any>,
    flags?: Partial<EnergyTrackerFlags>,
    params?: Partial<EnergyTrackerParams>,
    shadingCallback?: ShadingCallback
  ) {

    this.powerKW = powerInput > 50 ? powerInput / 1000 : powerInput;
    this.trackerMap = trackerMap;
    this.panelInfoMap = panelInfoMap;
    this.panelData = new Map();

    this.flags = {
      geometry: true, clearSky: true, clouds: true, iam: true, temperature: true,
      shading: false, soiling: true, pr: true, clipping: false, pushZeroAtNight: false,
      ...(flags ?? {}),



    };

    this.params = {
      keepDays: 5, debug: false, ambientTempC: 10, cloudiness01: 0,
      iam_b0: 0.05, gamma_Pmp_perK: -0.0045, NOCT: 45, soilingLoss01: 0.03,
      PR_misc: 0.92, inverterLimitKW: null, ...(params ?? {}),
    };

    this.shadingCallback = this.defaultShadingCallback;
    this.dirTypeTotals = this.initMatrix();
  }

  /* ───────────── utilities ───────────── */
  private initMatrix() {
    const m = new Map<string, number>();
    (['nord', 'ost', 'süd', 'west'] as DirKey[]).forEach(d =>
      (['fixed', 'single', 'limited', 'unlimited'] as TypeKey[]).forEach(t =>
        m.set(`${d}-${t}`, 0)
      )
    );
    return m;
  }

  private minutesOfDayFromDate(d: Date) {
    return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
  }

  private normalizedSunDir(s: THREE.Vector3) {
    return s.lengthSq() > 0 ? s.clone().normalize() : new THREE.Vector3(0, 0, 0);
  }

  private panelNormalFromQuaternion(q: THREE.Quaternion) {
    return new THREE.Vector3(0, 1, 0).applyQuaternion(q).normalize();
  }



    private normalizeDir(v?: string): NESW | null {
      if (!v) return null;
      const s = v.toLowerCase().trim();
      if (['n', 'north', 'nord'].includes(s)) return 'N';
      if (['e', 'east', 'ost', 'öst', 'oe', 'osten'].includes(s)) return 'E';
      if (['s', 'south', 'sued', 'süd', 'sueden'].includes(s)) return 'S';
      if (['w', 'west', 'westen'].includes(s)) return 'W';
      return null;
    }
    private readDirFromPanel(p: any): NESW | null {
      return this.normalizeDir(String(p?.alignment ?? p?.compass ?? p?.dir ?? p?.direction ?? ''));
    }
    private dirKeyFromNESW(n: NESW): DirKey {
      return n === 'N' ? 'nord' : n === 'E' ? 'ost' : n === 'S' ? 'süd' : 'west';
    }
    private normalizeType(v?: string): TypeKey | null {
      if (!v) return null;
      const s = v.toLowerCase().replace(/\s|-/g, '');
      if (s.startsWith('fix') || s.includes('static')) return 'fixed';
      if (s.includes('single') || s.includes('1axis') || s.includes('oneaxis')) return 'single';
      if (s.includes('limited') || s.includes('dualaxislimited') || s.includes('2axisltd')) return 'limited';
      if (s.includes('unlimited') || s.includes('dualaxis') || s.includes('2axis') || s.includes('dualtracker')) return 'unlimited';
      return null;
    }
    private readTypeFromPanel(p: any): TypeKey | null {
      return this.normalizeType(String(p?.type ?? ''));
    }

    /* ───────────── Faktoren ───────────── */

    geometryFactor(panelNormal: THREE.Vector3, sun: THREE.Vector3) {
      if (!this.flags.geometry) return 1;
      return Math.max(0, panelNormal.dot(sun));
    }

    airMassFromSun(sun: THREE.Vector3) {
      const sinH = Math.max(0, sun.y);
      if (sinH <= 0) return Infinity;
      const hDeg = Math.asin(sinH) * 180 / Math.PI;
      return 1 / (sinH + 0.50572 * Math.pow(hDeg + 6.07995, -1.6364));
    }

    clearSkyFactor(sun: THREE.Vector3) {
      if (!this.flags.clearSky) return 1;
      const am = this.airMassFromSun(sun);
      if (!isFinite(am)) return 0;
      return Math.pow(0.7, Math.pow(am, 0.678));
    }

    cloudFactor() {
      if (!this.flags.clouds) return 1;
      return 1 - 0.75 * clamp01(this.params.cloudiness01);
    }

    iamFactor(cosTheta: number) {
      if (!this.flags.iam) return 1;
      const c = Math.min(1, Math.max(0, cosTheta));
      if (c < 1e-6) return 0;
      return Math.max(0, 1 - this.params.iam_b0 * (1 / c - 1));
    }

    private cellTempNOCT(poa_kWm2: number, Tamb: number) {
      const E = poa_kWm2 * 1000; // W/m²
      return Tamb + (this.params.NOCT - 20) * (E / 800);
    }
    temperatureFactor(poa_kWm2: number) {
      if (!this.flags.temperature) return 1;
      const Tcell = this.cellTempNOCT(poa_kWm2, this.params.ambientTempC);
      return Math.max(0, 1 + this.params.gamma_Pmp_perK * (Tcell - 25));
    }





  /** Setzt die Occluder-Meshes für Schatten-Raycasting (von setupScene übergeben) */
  setOccluders(meshes: THREE.Mesh[]) {
    const uniqueMeshes = Array.from(new Set(meshes.filter(Boolean)));
    this.shadowOccluders = uniqueMeshes.map((mesh) => ({
      mesh,
      box: this.computeOccluderBox(mesh, new THREE.Box3()),
      builderObjectId: this.getBuilderObjectId(mesh),
      dynamic: this.isDynamicOccluder(mesh),
    }));
    // Sicherstellen dass alle Matrizen aktuell sind
    uniqueMeshes.forEach(m => m.updateMatrixWorld(true));
  }

  private defaultShadingCallback(name: string, panel: any, sun: THREE.Vector3): number {
    return this.checkForShadow(panel, sun) ? 0 : 1;
  }

  private getBuilderObjectId(obj: THREE.Object3D | null | undefined): string | null {
    return obj?.userData?.__builderObjectId ?? null;
  }

  private isDynamicOccluder(mesh: THREE.Mesh): boolean {
    let obj: THREE.Object3D | null = mesh;
    while (obj) {
      if ((obj as any).userData?.__isPanelRoot) return true;
      obj = obj.parent;
    }
    return false;
  }

  private isOwnOccluder(panel: any, occ: ShadowOccluder): boolean {
    const panelBuilderId = this.getBuilderObjectId(panel);
    if (panelBuilderId && occ.builderObjectId === panelBuilderId) return true;

    let obj: THREE.Object3D | null = occ.mesh;
    while (obj) {
      if (obj === panel) return true;
      obj = obj.parent;
    }
    return false;
  }

  private computeOccluderBox(mesh: THREE.Mesh, target: THREE.Box3) {
    const geometry = mesh.geometry as THREE.BufferGeometry | undefined;
    if (!geometry) return target.makeEmpty();
    if (!geometry.boundingBox) geometry.computeBoundingBox();
    if (!geometry.boundingBox) return target.makeEmpty();

    mesh.updateWorldMatrix(true, false);
    return target.copy(geometry.boundingBox).applyMatrix4(mesh.matrixWorld);
  }

  private checkForShadow(panel: any, sunDir: THREE.Vector3): boolean {
    if (this.shadowOccluders.length === 0) return false;
    // sunDir ist ein normierter Richtungsvektor VON der Szene ZUR Sonne (Directional Light).
    // Korrekte Methode: Strahl vom Panel in Richtung Sonne schießen.
    // Kleiner Offset damit das Panel sich nicht selbst trifft.
    if (panel?.getWorldPosition) panel.getWorldPosition(this._shadowOrigin);
    else if (panel?.position) this._shadowOrigin.copy(panel.position);
    else return false;

    this._shadowOrigin.addScaledVector(sunDir, 0.5);
    this._raycaster.set(this._shadowOrigin, sunDir);
    this._raycaster.near = 0;
    this._raycaster.far = 10000;
    this._shadowRay.copy(this._raycaster.ray);

    for (const occ of this.shadowOccluders) {
      if (this.isOwnOccluder(panel, occ)) continue;
      const box = occ.dynamic ? this.computeOccluderBox(occ.mesh, occ.box) : occ.box;
      if (box.isEmpty() || !this._shadowRay.intersectsBox(box)) continue;

      this._shadowHits.length = 0;
      this._raycaster.intersectObject(occ.mesh, false, this._shadowHits);
      if (this._shadowHits.length > 0) return true;
    }
    return false;
  }

  private shadingFactor(name: string, panel: any, sun: THREE.Vector3): number {
    if (!this.flags.shading) return 1;
    const shadowed = this.checkForShadow(panel, sun);
    if (this.params.debug) console.log(`[shading] ${name}: ${shadowed ? 'IN SHADOW' : 'clear'}`);
    return shadowed ? 0 : 1;
  }

  private computeTickEnergy(panelName: string, panel: any, sun: THREE.Vector3, deltaHours: number): number {
      if (deltaHours <= 0 || sun.y <= 0) return 0;

      const n = this.panelNormalFromQuaternion(panel.quaternion as THREE.Quaternion);
      const f_geom_raw = n.dot(sun);
      const f_geom = Math.max(0, f_geom_raw);
      const f_clear = this.clearSkyFactor(sun);
      const f_cloud = this.cloudFactor();
      const f_iam = this.iamFactor(f_geom);

      const poa_kWm2 = f_clear * f_cloud * f_geom;
      const f_temp = this.temperatureFactor(poa_kWm2);

      // Debug log for shading factor in energy computation
      const f_shade = this.shadingFactor(panelName, panel, sun);
      if (this.params.debug) console.log(`${panelName} shading factor: ${f_shade}`);
      if (f_shade === 0) {
        if (this.params.debug) console.log(`${panelName} is in shadow! No energy generated.`);
        return 0;  // No energy if shadowed
      }

      const f_soil = this.soilingFactor();
      const f_pr = this.prFactor();

      // powerKW ist die Leistung des Standard-Trackers (4 Module, Anthrazit).
      // Abweichende Bestückung skaliert linear; farbige Module haben einen
      // geringeren relativen Wirkungsgrad (panelEfficiency < 1).
      const f_config = ((panel.panelCount ?? 4) / 4) * (panel.panelEfficiency ?? 1);

      let P_tick_kW = this.powerKW * f_config * f_geom * f_clear * f_cloud * f_iam * f_temp * f_shade * f_soil * f_pr;
      P_tick_kW = this.applyClipping(P_tick_kW);

      if (this.params.debug) {
        console.log(`[${panelName}] P=${this.powerKW.toFixed(3)}kW Δh=${deltaHours.toFixed(4)} cfg=${f_config.toFixed(3)} geom=${f_geom.toFixed(3)} clr=${f_clear.toFixed(3)} cld=${f_cloud.toFixed(3)} iam=${f_iam.toFixed(3)} tmp=${f_temp.toFixed(3)} shd=${f_shade.toFixed(3)} soil=${f_soil.toFixed(3)} pr=${f_pr.toFixed(3)} → P_tick=${P_tick_kW.toFixed(3)}kW`);
      }

      return P_tick_kW * deltaHours; // kWh
    }

  soilingFactor() {
    if (!this.flags.soiling) return 1;
    return 1 - Math.min(1, Math.max(0, this.params.soilingLoss01));
  }

  prFactor() {
    if (!this.flags.pr) return 1;
    return Math.min(1, Math.max(0, this.params.PR_misc));
  }

  applyClipping(powerKW: number) {
    if (!this.flags.clipping || this.params.inverterLimitKW == null) return powerKW;
    return Math.min(powerKW, Math.max(0, this.params.inverterLimitKW));
  }

  /* ───────────── Public API ───────────── */
  setFlags(patch: Partial<EnergyTrackerFlags>) { this.flags = { ...this.flags, ...patch }; }
  setParams(patch: Partial<EnergyTrackerParams>) { this.params = { ...this.params, ...patch }; }
  setPowerKW(pKW: number) { this.powerKW = Math.max(0, pKW); }
  setPowerW(pW: number) { this.powerKW = Math.max(0, pW) / 1000; }

  getDirTypeTotals() { return new Map(this.dirTypeTotals); }

  updateAll(sunDir: THREE.Vector3, deltaHours: number, timestamp: Date) {
    if (deltaHours <= 0) return;

    const sun = this.normalizedSunDir(sunDir);
    if (sun.lengthSq() === 0) return;

    // Tageswechsel-Handling
    const currentDay = timestamp.getDate();
    if (currentDay !== this.lastUpdatedDay) {
      this.simulationDay++;
      this.resetDailyEnergy();
      this.lastUpdatedDay = currentDay;
      if (this.params.debug) console.log(`🗓️ Tageswechsel → SimTag ${this.simulationDay}`);
    }

    const minutesOfDay = this.minutesOfDayFromDate(timestamp);

    this.trackerMap.forEach((panel, name) => {
      if (!panel?.name || !panel?.type || !(panel.quaternion instanceof THREE.Quaternion)) return;

      const energy = this.computeTickEnergy(name, panel, sun, deltaHours);

      // PanelEntry sicherstellen
      if (!this.panelData.has(panel.name)) {
        this.panelData.set(panel.name, { type: panel.type, energyHistory: [], totalEnergy: 0, days: new Set() });
      }
      const entry = this.panelData.get(panel.name)!;

      // FIFO (tageweise) — O(1) dank days-Set statt O(n) Array-Scan
      if (!entry.days.has(this.simulationDay) && entry.days.size >= this.params.keepDays) {
        const oldest = Math.min(...entry.days);
        entry.energyHistory = entry.energyHistory.filter(e => e.day !== oldest);
        entry.days.delete(oldest);
      }

      // Historie
      if (energy > 0 || this.flags.pushZeroAtNight) {
        const h = timestamp.getHours(), m = timestamp.getMinutes(), s = timestamp.getSeconds();
        entry.energyHistory.push({
          time: `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`,
          minutesOfDay,
          energy,
          deltaHours,
          powerKW: deltaHours > 0 ? energy / deltaHours : 0,
          day: this.simulationDay
        });
        entry.days.add(this.simulationDay);
      }

      // Summen
      if (energy > 0) {
        entry.totalEnergy             += energy;
        panel.energyDay                = (panel.energyDay   ?? 0) + energy;
        panel.energyTotal              = (panel.energyTotal ?? 0) + energy;

        // ⇨ Matrix (Richtung × Typ) inkrementell mitführen
        const nd = this.readDirFromPanel(panel);
        const nt = this.readTypeFromPanel(panel);
        if (nd && nt) {
          const key = `${this.dirKeyFromNESW(nd)}-${nt}`;
          this.dirTypeTotals.set(key, (this.dirTypeTotals.get(key) ?? 0) + energy);
        }
      }

      panel.energyHistory = entry.energyHistory;

      const panelInfo = this.panelInfoMap.get(name);
      if (panelInfo?.isVisible?.()) panelInfo.update(panel);
    });

    // UI sofort informieren
    this.onMatrixUpdate?.(this.getDirTypeTotals());
  }

  resetDailyEnergy() {
    this.trackerMap.forEach(panel => {
      panel.energyDay = 0;
      const panelInfo = this.panelInfoMap.get(panel.name);
      if (panelInfo?.isVisible?.()) panelInfo.update(panel);
    });
  }

  resetAllTotals() {
    this.panelData.clear();
    this.trackerMap.forEach(panel => {
      panel.energyDay = 0;
      panel.energyTotal = 0;
      panel.energyHistory = [];
    });
    this.dirTypeTotals = this.initMatrix();
    this.lastUpdatedDay = -1;
    this.simulationDay = 0;
    if (this.params.debug) console.log('▶️ Totals & History & Matrix zurückgesetzt.');
    this.onMatrixUpdate?.(this.getDirTypeTotals());
  }

  getTotalEnergy(panelName: string) { return this.panelData.get(panelName)?.totalEnergy ?? 0; }
  getEnergyHistory(panelName: string) { return this.panelData.get(panelName)?.energyHistory ?? []; }
  getAllTotalEnergy() {
    const o: Record<string, number> = {};
    this.panelData.forEach((e, n) => { o[n] = e.totalEnergy; });
    return o;
  }
}
